import { SuspenseSelect } from '@/components/Suspense';
import { useGetInvoiceFilesReportStatuses } from '@/context/services/reportsType/invoiceReports/Edit.service';
import type { AdminInvoiceReportsSearchFormData } from '@/features/admin_invoice_reports/types';
import {
  FormSelect,
  type LabelProps,
} from '@moneyforward/ap-frontend-components';
import { FC, memo, useEffect, useMemo } from 'react';
import type { Control, FieldPath, UseFormReturn } from 'react-hook-form';

export type PropsControl = {
  control: Control<AdminInvoiceReportsSearchFormData>;
  name: FieldPath<AdminInvoiceReportsSearchFormData>;
  labelProps?: LabelProps;
  formContext?: UseFormReturn<AdminInvoiceReportsSearchFormData>;
};

const _AdminInvoiceMFFileStatusesSelect: FC<PropsControl> = memo(
  ({ control, name, labelProps, formContext }) => {
    const { data } = useGetInvoiceFilesReportStatuses();
    const currentValue = formContext?.getValues(name);

    const memoizedMFFileStatuses = useMemo(() => {
      return (
        data?.data?.map((status) => ({
          label: status.name ?? '',
          value: status.value ?? '',
        })) ?? []
      );
    }, [data?.data]);

    useEffect(() => {
      if (!currentValue || !memoizedMFFileStatuses?.length) return;
      const isValid = memoizedMFFileStatuses?.some(
        (option) => option.value === currentValue
      );
      if (!isValid) {
        // mf_file is the only field this select targets; admin form types it as `... | null`.
        formContext?.setValue(name, null);
      }
    }, [currentValue, memoizedMFFileStatuses, formContext, name]);

    const selectProps = useMemo(
      () => ({
        options: memoizedMFFileStatuses,
        label: labelProps,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        size: 'small' as const,
      }),
      [labelProps, memoizedMFFileStatuses]
    );

    return (
      <FormSelect<AdminInvoiceReportsSearchFormData>
        control={control}
        name={name}
        inputId={name}
        testId={name}
        selectProps={selectProps}
      />
    );
  }
);

_AdminInvoiceMFFileStatusesSelect.displayName =
  '_AdminInvoiceMFFileStatusesSelect';

export const AdminInvoiceMFFileStatusesSelect: FC<PropsControl> = memo(
  (props) => (
    <SuspenseSelect>
      <_AdminInvoiceMFFileStatusesSelect {...props} />
    </SuspenseSelect>
  )
);

AdminInvoiceMFFileStatusesSelect.displayName =
  'AdminInvoiceMFFileStatusesSelect';
